import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faImage, faDownload, faTimes,
    faChevronRight, faChevronLeft,
} from '@fortawesome/free-solid-svg-icons';
import { sortSizes, uid } from './utils/catalogUtils';

const ProductCard = ({
    product,
    isSelected,
    cart,
    setCart,
    setSelectedProducts,
    onToggleSelection,
    onDownloadImage,
    onUpdateCartItem,
    getAvailableQuantities,
    getPriceForQuantity,
    expandedProductId,
    onSetExpanded
}) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [inlineConfig, setInlineConfig] = useState({ color: '', size: '', quantity: '' });
    const [configRows, setConfigRows] = useState([
        { id: uid(), color: '', size: '', quantity: '' }
    ]);
    const mainImageRef = useRef(null);
    const cardRef = useRef(null);

    const images = product.imagenes || [];
    const hasMultipleImages = images.length > 1;
    const isExpanded = expandedProductId === product.id;
    const hasColorVariant = product.colores?.length > 0;
    const hasSizeVariant = product.tallas?.length > 0;
    const hasOptions = hasColorVariant || hasSizeVariant || getAvailableQuantities(product).length > 0;

    // Restaurar filas desde el carrito cuando el producto se selecciona
    useEffect(() => {
        if (!isSelected) return;
        const savedConfig = cart.find(item =>
            item.productId === product.id || item.originalProductId === product.id
        );
        if (!savedConfig) return;

        if (savedConfig.colorName?.includes(';')) {
            const colors = savedConfig.colorName.split(';');
            const sizes = savedConfig.sizeName ? savedConfig.sizeName.split(';') : [];
            const quantities = savedConfig.individualQuantities ? savedConfig.individualQuantities.split(';') : [];
            const rows = colors.map((color, i) => ({
                id: uid(),
                color: color || '',
                size: sizes[i] || '',
                quantity: quantities[i] || ''
            }));
            rows.push({ id: uid(), color: '', size: '', quantity: '' });
            setConfigRows(rows);
        } else if (savedConfig.colorName || savedConfig.sizeName || savedConfig.quantity) {
            setConfigRows([
                { id: uid(), color: savedConfig.colorName || '', size: savedConfig.sizeName || '', quantity: savedConfig.quantity || '' },
                { id: uid(), color: '', size: '', quantity: '' }
            ]);
        }

        setInlineConfig({
            color: savedConfig.colorName || '',
            size: savedConfig.sizeName || '',
            quantity: savedConfig.quantity || 0
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSelected, product.id]);

    // Resetear al deseleccionar
    useEffect(() => {
        if (!isSelected) {
            setInlineConfig(prev => ({ ...prev, quantity: 0 }));
            setConfigRows([{ id: uid(), color: '', size: '', quantity: '' }]);
        }
    }, [isSelected]);

    const handleRowChange = (rowId, field, value) => {
        const isSingleVariant = (hasColorVariant && !hasSizeVariant) || (!hasColorVariant && hasSizeVariant);

        // Calcular las filas actualizadas
        const updatedRows = configRows.map(row =>
            row.id === rowId ? { ...row, [field]: value } : row
        );

        const modifiedRow = updatedRows.find(row => row.id === rowId);
        const isComplete = modifiedRow &&
            (!hasColorVariant || modifiedRow.color) &&
            (!hasSizeVariant || modifiedRow.size) &&
            modifiedRow.quantity;

        // Agregar fila vacía si la última quedó completa
        const isLastRow = updatedRows[updatedRows.length - 1].id === rowId;
        if (isComplete && isLastRow) {
            if (isSingleVariant) {
                const selected = updatedRows.map(r => hasColorVariant ? r.color : r.size).filter(Boolean);
                const all = hasColorVariant ? product.colores : product.tallas;
                if (selected.length < all.length) {
                    updatedRows.push({ id: uid(), color: '', size: '', quantity: '' });
                }
            } else {
                updatedRows.push({ id: uid(), color: '', size: '', quantity: '' });
            }
        }

        // Actualizar filas primero
        setConfigRows(updatedRows);

        // Actualizar carrito directamente, sin setTimeout
        if (isComplete) {
            try {
                const allValid = updatedRows.filter(row => {
                    if (!row.quantity) return false;
                    if (hasColorVariant && !row.color) return false;
                    if (hasSizeVariant && !row.size) return false;
                    return true;
                });

                const totalQty = allValid.reduce((sum, row) => sum + (parseInt(row.quantity) || 0), 0);
                const unitPrice = totalQty > 0 ? getPriceForQuantity(product, totalQty) : 0;
                const qty = parseInt(modifiedRow.quantity) || 0;

                const cartItem = {
                    productId: `${product.id}_${rowId}`,
                    originalProductId: product.id,
                    productName: product.nombre,
                    productImageUrl: product.imagenes?.[0] || '',
                    colorName: modifiedRow.color || '',
                    sizeName: modifiedRow.size || '',
                    quantity: qty,
                    unitPrice,
                    totalPrice: unitPrice * qty,
                    individualQuantities: qty.toString(),
                    rowId
                };


                setCart(prevCart => {
                    const idx = prevCart.findIndex(item => item.rowId === rowId);
                    if (idx > -1) {
                        const nextCart = [...prevCart];
                        nextCart[idx] = cartItem;
                        return nextCart;
                    }
                    const nextCart = [...prevCart, cartItem];
                    return nextCart;
                });

                if (!isSelected) {
                    setSelectedProducts(prev => {
                        const next = new Set(prev);
                        next.add(product.id);
                        return next;
                    });
                }
            } catch (err) {
                console.error(`❌ [${product.nombre}] Error al agregar al carrito:`, err);
            }
        }
    };

    const handleRemoveRow = (rowId) => {
        const updatedRows = configRows.filter(row => row.id !== rowId);
        if (updatedRows.length === 0) {
            updatedRows.push({ id: uid(), color: '', size: '', quantity: '' });
        }

        setConfigRows(updatedRows);

        setCart(prevCart => prevCart.filter(item => item.rowId !== rowId));

        const hasValidRows = updatedRows.some(row =>
            row.quantity &&
            (!hasColorVariant || row.color) &&
            (!hasSizeVariant || row.size)
        );

        if (!hasValidRows) {
            setSelectedProducts(prev => {
                const next = new Set(prev);
                next.delete(product.id);
                return next;
            });
        }
    };

    // Actualizar carrito para productos sin variantes — llamado directo, sin useEffect
    const handleQuantityChange = (qty) => {
        const quantity = parseInt(qty);
        if (quantity === 0) {
            if (isSelected) onToggleSelection(product, currentImageIndex);
            if (expandedProductId === product.id) onSetExpanded(null);
            setInlineConfig(prev => ({ ...prev, quantity: 0 }));
        } else {
            setInlineConfig(prev => ({ ...prev, quantity }));
            if (!isSelected) onToggleSelection(product, currentImageIndex);
            try {
                const unitPrice = getPriceForQuantity(product, quantity);
                onUpdateCartItem(product, inlineConfig.color, inlineConfig.size, quantity, unitPrice, quantity.toString());
            } catch (err) {
                console.error(`❌ [${product.nombre}] Error en handleQuantityChange:`, err);
            }
            if (hasOptions && (hasColorVariant || hasSizeVariant)) {
                onSetExpanded(product.id);
                setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
            }
        }
    };

    const handleCardClick = (e) => {
        const isInteractive = e.target.closest('button, select, input, .private-catalog-arrow-btn, .private-catalog-download-btn, .private-catalog-indicator, .inline-collapse-toggle');
        if (!isInteractive && isSelected) {
            onSetExpanded(expandedProductId === product.id ? null : product.id);
        }
    };

    const goToPreviousImage = () => currentImageIndex > 0 && setCurrentImageIndex(prev => prev - 1);
    const goToNextImage = () => currentImageIndex < images.length - 1 && setCurrentImageIndex(prev => prev + 1);

    // Gestos táctiles
    useEffect(() => {
        if (!imgLoaded || !hasMultipleImages) return;

        let startX = 0, currentX = 0, isDragging = false, touchStartY = 0;

        const onStart = (e) => {
            startX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
            currentX = startX;
            isDragging = false;
        };

        const onMove = (e) => {
            currentX = e.touches[0].clientX;
            const diffX = Math.abs(currentX - startX);
            const diffY = Math.abs(e.touches[0].clientY - touchStartY);
            if (!isDragging && diffX > 10 && diffX > diffY * 1.5) isDragging = true;
            if (isDragging) e.preventDefault();
        };

        const onEnd = (e) => {
            if (!isDragging) return;
            const diff = startX - e.changedTouches[0].clientX;
            if (Math.abs(diff) > 50) {
                if (diff > 0 && currentImageIndex < images.length - 1) setCurrentImageIndex(prev => prev + 1);
                else if (diff < 0 && currentImageIndex > 0) setCurrentImageIndex(prev => prev - 1);
            }
            isDragging = false;
        };

        const el = mainImageRef.current;
        if (el) {
            el.addEventListener('touchstart', onStart, { passive: true });
            el.addEventListener('touchmove', onMove, { passive: false });
            el.addEventListener('touchend', onEnd, { passive: true });
            return () => {
                el.removeEventListener('touchstart', onStart);
                el.removeEventListener('touchmove', onMove);
                el.removeEventListener('touchend', onEnd);
            };
        }
    }, [imgLoaded, hasMultipleImages, currentImageIndex, images.length]);

    return (
        <div
            ref={cardRef}
            className={`private-catalog-product-card ${isSelected ? 'selected' : ''} ${isExpanded ? 'expanded' : ''}`}
            onClick={handleCardClick}
            style={{ cursor: 'pointer' }}
        >
            <div className="private-catalog-image-container">
                <div className="private-catalog-image-controls">
                    <div className="private-catalog-controls-left">
                        <button
                            className="private-catalog-download-btn"
                            onClick={(e) => { e.stopPropagation(); onDownloadImage(images[currentImageIndex], product.nombre, currentImageIndex); }}
                            aria-label="Descargar imagen"
                        >
                            <FontAwesomeIcon icon={faDownload} />
                        </button>
                    </div>
                    <div className="private-catalog-controls-right" />
                </div>

                <div className="private-catalog-carousel-container">
                    {images.length > 0 ? (
                        <div className="private-catalog-image-wrapper">
                            {hasMultipleImages && currentImageIndex > 0 && (
                                <button className="private-catalog-arrow-btn private-catalog-arrow-left" onClick={(e) => { e.stopPropagation(); goToPreviousImage(); }} aria-label="Imagen anterior">
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </button>
                            )}
                            {hasMultipleImages && currentImageIndex < images.length - 1 && (
                                <button className="private-catalog-arrow-btn private-catalog-arrow-right" onClick={(e) => { e.stopPropagation(); goToNextImage(); }} aria-label="Siguiente imagen">
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            )}
                            <div
                                ref={mainImageRef}
                                className="private-catalog-carousel-wrapper"
                                style={{
                                    width: `${images.length * 100}%`,
                                    transform: `translateX(-${currentImageIndex * (100 / images.length)}%)`,
                                    transition: imgLoaded ? 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
                                }}
                            >
                                {images.map((img, index) => (
                                    <div key={index} className="private-catalog-carousel-slide" style={{ width: `${100 / images.length}%` }}>
                                        <img
                                            src={img}
                                            alt={`${product.nombre} ${index + 1}`}
                                            className="private-catalog-product-image"
                                            onLoad={() => index === 0 && setImgLoaded(true)}
                                            loading="lazy"
                                        />
                                    </div>
                                ))}
                            </div>
                            {hasMultipleImages && (
                                <div className="private-catalog-image-indicators">
                                    {images.map((_, index) => (
                                        <button
                                            key={index}
                                            className={`private-catalog-indicator ${index === currentImageIndex ? 'active' : ''}`}
                                            onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(index); }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="private-catalog-no-image">
                            <FontAwesomeIcon icon={faImage} />
                        </div>
                    )}
                </div>
            </div>

            <h3 className="private-catalog-product-name">{product.nombre}</h3>
            {product.descripcion && <p className="private-catalog-product-description">{product.descripcion}</p>}
            {product.precios && Object.keys(product.precios).length > 0 && (
                <div className="private-catalog-product-price">
                    ${Math.max(...Object.values(product.precios)).toLocaleString()}
                </div>
            )}

            {/* Selector de variantes inline */}
            {hasOptions && (hasColorVariant || hasSizeVariant) && (
                <div className="private-catalog-inline-options" onClick={(e) => e.stopPropagation()}>
                    <div className="inline-options-content">
                        {configRows.map((row) => {
                            const isEmptyRow = !row.color && !row.size && !row.quantity;
                            const isSingleVariant = (hasColorVariant && !hasSizeVariant) || (!hasColorVariant && hasSizeVariant);
                            const isRowComplete = (!hasColorVariant || row.color) && (!hasSizeVariant || row.size) && row.quantity;

                            let availableColors = product.colores || [];
                            let availableSizes = product.tallas || [];

                            if (isSingleVariant) {
                                if (hasColorVariant) {
                                    const selected = configRows.filter(r => r.id !== row.id && r.color).map(r => r.color);
                                    availableColors = product.colores.filter(c => !selected.includes(c) || c === row.color);
                                } else {
                                    const selected = configRows.filter(r => r.id !== row.id && r.size).map(r => r.size);
                                    availableSizes = sortSizes(product.tallas).filter(s => !selected.includes(s) || s === row.size);
                                }
                            } else {
                                availableSizes = sortSizes(product.tallas);
                            }

                            return (
                                <div key={row.id} className={`inline-config-row ${isRowComplete ? 'complete' : ''}`}>
                                    {hasColorVariant && (
                                        <div className="inline-option-group">
                                            <label>Color:</label>
                                            <select value={row.color} onChange={(e) => { e.stopPropagation(); handleRowChange(row.id, 'color', e.target.value); }} onClick={(e) => e.stopPropagation()} className={!row.color && (row.size || row.quantity) ? 'incomplete' : ''}>
                                                <option value="">Seleccionar...</option>
                                                {availableColors.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                    )}
                                    {hasSizeVariant && (
                                        <div className="inline-option-group">
                                            <label>Talla:</label>
                                            <select value={row.size} onChange={(e) => { e.stopPropagation(); handleRowChange(row.id, 'size', e.target.value); }} onClick={(e) => e.stopPropagation()} className={!row.size && (row.color || row.quantity) ? 'incomplete' : ''}>
                                                <option value="">Seleccionar...</option>
                                                {availableSizes.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>
                                    )}
                                    <div className="inline-option-group">
                                        <label>Cantidad:</label>
                                        <select value={row.quantity} onChange={(e) => { e.stopPropagation(); handleRowChange(row.id, 'quantity', e.target.value); }} onClick={(e) => e.stopPropagation()} className={!row.quantity && (row.color || row.size) ? 'incomplete' : ''}>
                                            <option value="">Seleccionar...</option>
                                            {getAvailableQuantities(product).map(qty => (
                                                <option key={qty} value={qty}>{qty} {qty === 1 ? 'unidad' : 'unidades'}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {configRows.filter(r => r.color || r.size || r.quantity).length > 1 && !isEmptyRow && (
                                        <button className="inline-remove-row-btn" onClick={(e) => { e.stopPropagation(); handleRemoveRow(row.id); }} aria-label="Eliminar fila">
                                            <FontAwesomeIcon icon={faTimes} />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Selector simple para productos sin variantes */}
            {!hasColorVariant && !hasSizeVariant && (
                <div className="private-catalog-quantity-selector" onClick={(e) => e.stopPropagation()}>
                    <label className="private-catalog-quantity-label">Cantidad:</label>
                    <select
                        value={inlineConfig.quantity || 0}
                        onChange={(e) => { e.stopPropagation(); handleQuantityChange(e.target.value); }}
                        onClick={(e) => e.stopPropagation()}
                        className={`private-catalog-quantity-select ${isSelected ? 'selected' : ''}`}
                    >
                        <option value="0">0 - No seleccionado</option>
                        {getAvailableQuantities(product).map(qty => (
                            <option key={qty} value={qty}>{qty} {qty === 1 ? 'unidad' : 'unidades'}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Modal de opciones eliminado - ya no se usa */}
        </div>
    );
};

export default ProductCard;
